import { useState, useRef } from "react";
import {
	Box,
	Button,
	IconButton,
	Typography,
	useTheme,
	useMediaQuery,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Alert,
	Menu,
	MenuItem,
	ListItemIcon,
	ListItemText,
	Divider,
} from "@mui/material";
import {
	Add,
	ContentCopy,
	Delete,
	Download,
	Upload,
	Merge,
	Publish,
	Map,
	Warning,
	CallSplit,
	Edit,
	BorderColor,
	MoreVert,
	SelectAll,
} from "@mui/icons-material";
import {
	MRT_GlobalFilterTextField,
	MRT_ToggleFiltersButton,
	MRT_ToggleFullScreenButton,
	MRT_TableInstance,
} from "material-react-table";
import type { Job } from "../../../../../models";
import type { CSVImportResult, CSVImportSummary } from "../ImportTable.csv";
import { PortalTooltip } from "../../../../helpers";
import { useEditModeContext } from "../shared/hooks/EditModeContext";

/**
 * Props interface for ImportTableToolbar component
 */
export interface ImportTableToolbarProps {
	// Table instance
	table: MRT_TableInstance<Job>;

	// State
	jobs: Job[];

	// Map controls - new mode-based API
	mapDisplayMode?: "modal" | "drawer" | null;
	setMapDisplayMode?: (mode: "modal" | "drawer" | null) => void;

	// Legacy map controls (for backward compatibility)
	isMapVisible?: boolean;
	setIsMapVisible?: (visible: boolean) => void;

	hasSelectedRows?: boolean;
	selectedRowCount?: number;

	// Lasso selection state for Split feature
	selectedFeatureIds?: Set<string>;
	selectedJobId?: string;

	// CSV import
	importCSV: (file: File) => Promise<void>;
	csvImportPending: { result: CSVImportResult; summary: CSVImportSummary } | null;
	confirmCSVImport: () => void;
	cancelCSVImport: () => void;
	csvImportErrors: string[] | null;
	dismissCsvImportErrors: () => void;

	// Callbacks
	hasValidationErrors: () => boolean;
	exportCSV: () => void;
	createJobs: () => Promise<void>;
	addBlankJob: () => void;
	deleteSelectedJobs: () => void;
	duplicateSelectedJobs: () => void;
	mergeSelectedJobs: () => void;
	splitSelectedJob?: () => void;
	bulkEditSelectedJobs?: () => void;
}

/**
 * Toolbar component for ImportTable with global search, filters, and action buttons.
 *
 * Features:
 * - Global search field
 * - Filter toggle
 * - Fullscreen toggle
 * - CSV export button
 * - Create Jobs button (disabled when validation errors exist)
 * - Map view buttons (modal and drawer options)
 * - Error message display when validation fails
 */
export const ImportTableToolbar: React.FC<ImportTableToolbarProps> = ({
	table,
	jobs,
	mapDisplayMode,
	setMapDisplayMode,
	isMapVisible,
	setIsMapVisible,
	hasSelectedRows,
	selectedRowCount = 0,
	selectedFeatureIds,
	importCSV,
	csvImportPending,
	confirmCSVImport,
	cancelCSVImport,
	csvImportErrors,
	dismissCsvImportErrors,
	hasValidationErrors,
	exportCSV,
	createJobs,
	addBlankJob,
	deleteSelectedJobs,
	duplicateSelectedJobs,
	mergeSelectedJobs,
	splitSelectedJob,
	bulkEditSelectedJobs,
}) => {
	// Determine if we're using the new or legacy API
	const hasNewMapAPI = setMapDisplayMode !== undefined;
	const hasLegacyMapAPI = setIsMapVisible !== undefined;
	const showMapControls = hasNewMapAPI || hasLegacyMapAPI;

	// Mobile detection for responsive map button display
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	// Edit mode context for "Edit All" button
	const { editingRowIds, enterAllEditMode, exitAllEditMode } = useEditModeContext();
	const isAnyRowEditing = editingRowIds.size > 0;

	// Overflow menu state
	const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
	const menuOpen = Boolean(menuAnchor);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
		setMenuAnchor(event.currentTarget);
	};
	const handleMenuClose = () => {
		setMenuAnchor(null);
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			importCSV(file);
			e.target.value = "";
		}
	};

	const handleModalClick = () => {
		if (hasNewMapAPI) {
			setMapDisplayMode(mapDisplayMode === "modal" ? null : "modal");
		} else if (hasLegacyMapAPI) {
			setIsMapVisible(!isMapVisible);
		}
	};

	const handleDrawerClick = () => {
		if (hasNewMapAPI) {
			setMapDisplayMode(mapDisplayMode === "drawer" ? null : "drawer");
		}
	};

	const isModalActive = hasNewMapAPI ? mapDisplayMode === "modal" : isMapVisible;
	const isDrawerActive = hasNewMapAPI ? mapDisplayMode === "drawer" : false;

	const canSplit = selectedRowCount === 1 && !!selectedFeatureIds?.size;

	return (
		<Box className={`toolbar-container${isDrawerActive ? " map-open" : ""}`}>
			<Box className="toolbar-left">
				{/* import MRT sub-components */}
				<MRT_GlobalFilterTextField table={table} />
				<MRT_ToggleFiltersButton table={table} />
				{hasValidationErrors() && (
					<>
						<Typography color="primary" className="error-message desktop-only">
							Double-click to edit
						</Typography>
						<Typography color="error" className="error-message desktop-only">
							Fill out all required data
						</Typography>
						<PortalTooltip title="Fill out all required data">
							<Warning color="error" className="error-icon mobile-only" />
						</PortalTooltip>
					</>
				)}
			</Box>
			<Box className="toolbar-right">
				<Box className="toolbar-actions">
					{/* Map view buttons - responsive: modal on mobile, drawer on desktop */}
					{showMapControls && hasNewMapAPI && (
						<>
							{/* Modal button - mobile only, hidden when drawer is open */}
							{isMobile && !isDrawerActive && (
								<PortalTooltip
									title={
										!hasSelectedRows
											? "Select jobs to view them on a map"
											: isModalActive
												? "Close map"
												: "Show jobs on map"
									}
								>
									<span>
										<IconButton
											color="primary"
											onClick={handleModalClick}
											disabled={!hasSelectedRows && !isModalActive}
										>
											<Map />
										</IconButton>
									</span>
								</PortalTooltip>
							)}
							{/* Drawer button - desktop only */}
							{!isMobile && (
								<PortalTooltip
									title={
										!hasSelectedRows
											? "Select jobs to view them on a map"
											: isDrawerActive
												? "Close map"
												: "Show jobs on map"
									}
								>
									<span style={{ display: "flex" }}>
										<Button
											color="primary"
											variant="text"
											onClick={handleDrawerClick}
											disabled={!hasSelectedRows && !isDrawerActive}
										>
											<Map className="icon-in-button" />
											<span className="button-text">Map</span>
										</Button>
									</span>
								</PortalTooltip>
							)}
						</>
					)}

					{/* Edit Mode toggle */}
					<PortalTooltip title={isAnyRowEditing ? "Exit edit mode" : "Enter edit mode"}>
						<Button
							color="primary"
							variant={isAnyRowEditing ? "contained" : "text"}
							onClick={(e) => {
								e.stopPropagation();
								if (isAnyRowEditing) {
									exitAllEditMode();
								} else {
									const allRowIds = table.getRowModel().flatRows.map((r) => r.id);
									enterAllEditMode(allRowIds);
								}
							}}
						>
							<BorderColor className="icon-in-button" />
							<span className="button-text">{isAnyRowEditing ? "Done" : "Edit Mode"}</span>
						</Button>
					</PortalTooltip>

					{/* Hidden file input for CSV upload */}
					<input ref={fileInputRef} type="file" accept=".csv" hidden onChange={handleFileChange} />

					{/* CSV download button */}
					<PortalTooltip title="Download table as CSV for editing. Upload the modified CSV to apply changes.">
						<span style={{ display: "flex" }}>
							<Button color="primary" variant="text" onClick={exportCSV} disabled={jobs.length === 0}>
								<Download className="icon-in-button" />
								<span className="button-text">Download CSV</span>
							</Button>
						</span>
					</PortalTooltip>
					{/* CSV upload button */}
					<PortalTooltip title="Upload a modified CSV to update job data">
						<span style={{ display: "flex" }}>
							<Button color="primary" variant="text" onClick={() => fileInputRef.current?.click()}>
								<Upload className="icon-in-button" />
								<span className="button-text">Upload CSV</span>
							</Button>
						</span>
					</PortalTooltip>

					{/* Overflow menu button */}
					<PortalTooltip title="More actions">
						<IconButton color="primary" onClick={handleMenuOpen}>
							<MoreVert />
						</IconButton>
					</PortalTooltip>

					{/* Overflow Menu */}
					<Menu
						anchorEl={menuAnchor}
						open={menuOpen}
						onClose={handleMenuClose}
						anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
						transformOrigin={{ vertical: "top", horizontal: "right" }}
					>
						{/* Edit Mode */}
						<MenuItem
							onClick={(e) => {
								e.stopPropagation();
								handleMenuClose();
								if (isAnyRowEditing) {
									exitAllEditMode();
								} else {
									const allRowIds = table.getRowModel().flatRows.map((r) => r.id);
									enterAllEditMode(allRowIds);
								}
							}}
						>
							<ListItemIcon>
								<BorderColor fontSize="small" />
							</ListItemIcon>
							<ListItemText>{isAnyRowEditing ? "Done Editing" : "Edit Mode"}</ListItemText>
						</MenuItem>
						{/* Download CSV */}
						<MenuItem
							onClick={() => {
								handleMenuClose();
								exportCSV();
							}}
							disabled={jobs.length === 0}
						>
							<ListItemIcon>
								<Download fontSize="small" />
							</ListItemIcon>
							<ListItemText>Download CSV</ListItemText>
						</MenuItem>
						{/* Upload CSV */}
						<MenuItem
							onClick={() => {
								handleMenuClose();
								fileInputRef.current?.click();
							}}
						>
							<ListItemIcon>
								<Upload fontSize="small" />
							</ListItemIcon>
							<ListItemText>Upload CSV</ListItemText>
						</MenuItem>
						<Divider />
						{/* Add blank job */}
						<MenuItem
							onClick={() => {
								handleMenuClose();
								addBlankJob();
							}}
						>
							<ListItemIcon>
								<Add fontSize="small" />
							</ListItemIcon>
							<ListItemText>Add Job</ListItemText>
						</MenuItem>
						{/* Select / Deselect All */}
						<MenuItem
							onClick={() => {
								handleMenuClose();
								table.toggleAllRowsSelected(!table.getIsAllRowsSelected());
							}}
						>
							<ListItemIcon>
								<SelectAll fontSize="small" />
							</ListItemIcon>
							<ListItemText>{table.getIsAllRowsSelected() ? "Deselect All" : "Select All"}</ListItemText>
						</MenuItem>
						{/* Duplicate Selected */}
						<MenuItem
							onClick={() => {
								handleMenuClose();
								duplicateSelectedJobs();
							}}
							disabled={!hasSelectedRows}
						>
							<ListItemIcon>
								<ContentCopy fontSize="small" />
							</ListItemIcon>
							<ListItemText>Duplicate{selectedRowCount > 0 ? ` Selected (${selectedRowCount})` : ""}</ListItemText>
						</MenuItem>
						{/* Bulk Edit Selected */}
						<MenuItem
							onClick={() => {
								handleMenuClose();
								bulkEditSelectedJobs?.();
							}}
							disabled={selectedRowCount < 2}
						>
							<ListItemIcon>
								<Edit fontSize="small" />
							</ListItemIcon>
							<ListItemText>Bulk Edit{selectedRowCount >= 2 ? ` Selected (${selectedRowCount})` : ""}</ListItemText>
						</MenuItem>
						{/* Merge Selected */}
						<MenuItem
							onClick={() => {
								handleMenuClose();
								mergeSelectedJobs();
							}}
							disabled={selectedRowCount < 2}
						>
							<ListItemIcon>
								<Merge fontSize="small" />
							</ListItemIcon>
							<ListItemText>Merge{selectedRowCount >= 2 ? ` Selected (${selectedRowCount})` : ""}</ListItemText>
						</MenuItem>
						{/* Split by Features */}
						<MenuItem
							onClick={() => {
								handleMenuClose();
								splitSelectedJob?.();
							}}
							disabled={!canSplit}
						>
							<ListItemIcon>
								<CallSplit fontSize="small" />
							</ListItemIcon>
							<ListItemText>Split by Features{canSplit ? ` (${selectedFeatureIds?.size})` : ""}</ListItemText>
						</MenuItem>
						<Divider />
						{/* Delete Selected - destructive, at bottom */}
						<MenuItem
							onClick={() => {
								handleMenuClose();
								deleteSelectedJobs();
							}}
							disabled={!hasSelectedRows}
						>
							<ListItemIcon>
								<Delete fontSize="small" color="error" />
							</ListItemIcon>
							<ListItemText primaryTypographyProps={{ color: "error" }}>
								Delete{selectedRowCount > 0 ? ` Selected (${selectedRowCount})` : ""}
							</ListItemText>
						</MenuItem>
					</Menu>

					{/* Submit Jobs button */}
					<PortalTooltip title="Submit jobs to Rogo">
						<span style={{ display: "flex" }}>
							<Button color="primary" variant="contained" onClick={createJobs} disabled={hasValidationErrors()}>
								<Publish className="icon-in-button" />
								<span className="button-text">Submit Jobs</span>
							</Button>
						</span>
					</PortalTooltip>
					<MRT_ToggleFullScreenButton table={table} />
				</Box>
			</Box>
			{/* CSV Import Confirmation Dialog */}
			<Dialog open={!!csvImportPending} onClose={cancelCSVImport}>
				<DialogTitle>Apply CSV Changes?</DialogTitle>
				<DialogContent>
					{csvImportPending && (
						<>
							<Typography>This will:</Typography>
							<ul>
								{csvImportPending.summary.updated > 0 && (
									<li>Update {csvImportPending.summary.updated} existing job(s)</li>
								)}
								{csvImportPending.summary.added > 0 && <li>Add {csvImportPending.summary.added} new job(s)</li>}
								{csvImportPending.summary.removed > 0 && <li>Remove {csvImportPending.summary.removed} job(s)</li>}
							</ul>
							{csvImportPending.summary.changedColumns.length > 0 && (
								<>
									<Typography sx={{ mt: 1 }}>Columns with changes:</Typography>
									<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
										{csvImportPending.summary.changedColumns.join(", ")}
									</Typography>
								</>
							)}
							{csvImportPending.summary.warnings.length > 0 && (
								<Alert severity="warning" sx={{ mt: 1 }}>
									{csvImportPending.summary.warnings.length} warning(s). Check data after import.
								</Alert>
							)}
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={cancelCSVImport}>Cancel</Button>
					<Button onClick={confirmCSVImport} variant="contained">
						Apply
					</Button>
				</DialogActions>
			</Dialog>
			{/* CSV Import Error Dialog */}
			<Dialog open={!!csvImportErrors} onClose={dismissCsvImportErrors}>
				<DialogTitle>CSV Import Failed</DialogTitle>
				<DialogContent>
					{csvImportErrors?.[0] && <Alert severity="error">{csvImportErrors[0]}</Alert>}
					{csvImportErrors?.slice(1).map((info, i) => (
						<Typography key={i} variant="body2" color="text.secondary" sx={{ mt: 1 }}>
							{info}
						</Typography>
					))}
				</DialogContent>
				<DialogActions>
					<Button onClick={dismissCsvImportErrors} variant="contained">
						OK
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};
